import { PositionStatus } from "swan-public-api-client";
import { FxConvertedPosition } from "./FxConvertedPosition";

const addOrUndefined = (x: number | undefined, y: number | undefined) => {
  if (x === undefined || y === undefined) {
    return undefined;
  }
  return x + y;
};

// Positions from the PAPI are keys by market, currency and direction.
// We combine the direction rows here.
export function groupPositionsByMarketAndCurrency(
  positions: FxConvertedPosition[]
): FxConvertedPosition[] {
  const groupedPositions = new Map<string, FxConvertedPosition[]>();

  positions.forEach(position => {
    const key = getByMarketAndCurrencyKey(position);
    if (!groupedPositions.has(key)) {
      groupedPositions.set(key, []);
    }

    groupedPositions.get(key)!.push(position);
  });

  return Array.from(groupedPositions.entries()).map(x => {
    const key = x[0];
    const positionsByMarketAndCurrency = x[1];
    const firstPosition = positionsByMarketAndCurrency[0];

    const quantity = positionsByMarketAndCurrency
      .map(p => p.Quantity)
      .reduce((accumulator, current) => accumulator + current);

    const absoluteQuantity = positionsByMarketAndCurrency
      .map(p => p.Quantity)
      .reduce((accumulator, current) => accumulator + Math.abs(current), 0);

    const currentPrice =
      positionsByMarketAndCurrency
        .map(p => p.CurrentPrice)
        .reduce((accumulator, current) => accumulator + current) /
      positionsByMarketAndCurrency.length;

    const openPnL = positionsByMarketAndCurrency
      .map(p => p.OpenPnL)
      .reduce((accumulator, current) => accumulator + current);

    const baseCurrencyOpenPnL = positionsByMarketAndCurrency
      .map(p => p.BaseCurrencyOpenPnL)
      .reduce((accumulator, current) => addOrUndefined(accumulator, current));

    const baseCurrencyMarginRequirement = positionsByMarketAndCurrency
      .map(p => p.BaseCurrencyMarginRequirement)
      .reduce((accumulator, current) => addOrUndefined(accumulator, current));

    const status = positionsByMarketAndCurrency
      .map(p => p.Status)
      .reduce((accumulator, current) => {
        if (accumulator === PositionStatus.TriggeredOrder) {
          return PositionStatus.TriggeredOrder;
        }
        if (accumulator === PositionStatus.Closed) {
          return PositionStatus.Closed;
        }

        return current;
      });

    const bookingPrice = positionsByMarketAndCurrency.reduce(
      (accumulator, current) =>
        accumulator +
        (current.BookingPrice * Math.abs(current.Quantity)) / absoluteQuantity,
      0
    );

    const combinedPosition: FxConvertedPosition = {
      PositionKey: { Key: key },
      AccountKey: firstPosition.AccountKey,
      TradingAccountKey: firstPosition.TradingAccountKey,
      MarketKey: firstPosition.MarketKey,
      MarketName: firstPosition.MarketName,
      Quantity: quantity,
      CurrentPrice: currentPrice,
      OpenPnL: openPnL,
      CurrencyId: firstPosition.CurrencyId,
      MarginRequirement: firstPosition.MarginRequirement, // margin requirement for both directions is the same, and is the total
      Status: status,
      BookingPrice: bookingPrice,
      BaseCurrencyOpenPnL: baseCurrencyOpenPnL,
      BaseCurrencyMarginRequirement: baseCurrencyMarginRequirement
    };
    return combinedPosition;
  });
}

function getByMarketAndCurrencyKey(position: FxConvertedPosition): string {
  return `${position.MarketKey}#${position.CurrencyId}`;
}
